// components/MediaUploader.jsx
import React, { useState, useRef } from 'react';
import { MDBIcon } from 'mdb-react-ui-kit';
import axios from 'axios';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png'];
const MAX_SIZE_MB = 5;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

const MediaUploader = ({ onUploadComplete }) => {
    const fileInputRef = useRef(null);
    const [preview, setPreview] = useState('');   // blob URL for instant preview
    const [fileName, setFileName] = useState('');
    const [fileSize, setFileSize] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [uploadedUrl, setUploadedUrl] = useState(''); // final S3 URL
    const [error, setError] = useState('');

    // ── helpers ──────────────────────────────────────────────────────────────
    const gymid = () => {
        try { return JSON.parse(localStorage.getItem('loggedUserInfo'))?.gymid || ''; }
        catch { return ''; }
    };

    const formatSize = (bytes) => {
        if (!bytes) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };

    const reset = () => {
        setPreview(''); setFileName(''); setFileSize(0);
        setUploadedUrl(''); setError('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // ── file selection & upload ───────────────────────────────────────────────
    const handleFile = async (file) => {
        if (!file) return;
        setError('');

        // Validate type
        if (!ACCEPTED_TYPES.includes(file.type)) {
            setError('Only JPEG and PNG images are supported.'); return;
        }
        // Validate size
        if (file.size > MAX_SIZE_BYTES) {
            setError(`Image must be smaller than ${MAX_SIZE_MB} MB.`); return;
        }

        // Instant local preview
        const blobUrl = URL.createObjectURL(file);
        setPreview(blobUrl);
        setFileName(file.name);
        setFileSize(file.size);

        // Upload to S3 via backend
        try {
            setUploading(true);
            const formData = new FormData();
            formData.append('file', file);
            formData.append('type', 'image');
            formData.append('purpose', 'template_header');
            let PRIMARY_SERVER_URL = `${window.location.protocol}//${window.location.hostname}/api/`;
            if (PRIMARY_SERVER_URL.includes("localhost")) {
                PRIMARY_SERVER_URL = process.env.REACT_APP_PRIMARY_SERVER_URL
            }
            const response = await axios.post(
                `${PRIMARY_SERVER_URL}whatsappservice/upload-media/${gymid()}`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            if (response.data?.url) {
                const s3Url = response.data.url;
                setUploadedUrl(s3Url);
                if (onUploadComplete) onUploadComplete(s3Url);
            } else {
                throw new Error('No URL returned from server.');
            }
        } catch (err) {
            console.error('Upload failed:', err);
            setError(err.response?.data?.message || 'Upload failed. Please try again.');
            reset();
        } finally {
            setUploading(false);
        }
    };

    const handleInputChange = (e) => handleFile(e.target.files?.[0]);
    const handleDrop = (e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); };
    const handleDragOver = (e) => e.preventDefault();

    // ── render ────────────────────────────────────────────────────────────────
    return (
        <div className="meta-uploader">

            {/* ── Empty / dropzone state ── */}
            {!preview && (
                <div
                    className="meta-image-dropzone"
                    onClick={() => fileInputRef.current?.click()}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                >
                    <div className="meta-image-dropzone-inner">
                        <div className="meta-image-dropzone-icon">
                            <MDBIcon fas icon="cloud-upload-alt" />
                        </div>
                        <div className="meta-image-dropzone-text">
                            <span className="meta-image-dropzone-title">Click to upload or drag &amp; drop</span>
                            <span className="meta-image-dropzone-sub">JPEG · PNG · up to {MAX_SIZE_MB} MB</span>
                        </div>
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png"
                        onChange={handleInputChange}
                        style={{ display: 'none' }}
                    />
                </div>
            )}

            {/* ── Preview card ── */}
            {preview && (
                <div className="meta-image-preview-card">
                    <img src={preview} alt="Preview" className="meta-image-preview-img" />

                    {/* overlay — spinner while uploading, actions when done */}
                    <div className="meta-image-preview-overlay">
                        {uploading ? (
                            <div className="meta-image-uploading">
                                <span className="spinner-border spinner-border-sm text-white" role="status" />
                                <span>Uploading…</span>
                            </div>
                        ) : (
                            <div className="meta-image-preview-actions">
                                <button
                                    type="button"
                                    className="meta-image-change-btn"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <MDBIcon fas icon="exchange-alt" /> Change
                                </button>
                                <button
                                    type="button"
                                    className="meta-image-remove-btn"
                                    onClick={reset}
                                >
                                    <MDBIcon fas icon="trash" /> Remove
                                </button>
                            </div>
                        )}
                    </div>

                    {/* file info bar */}
                    <div className="meta-image-file-info">
                        {uploadedUrl ? (
                            <>
                                <MDBIcon fas icon="check-circle" className="meta-image-file-ok" />
                                <span>{fileName}</span>
                                <span className="meta-image-file-size">({formatSize(fileSize)})</span>
                                <span className="meta-image-url-applied" style={{ marginLeft: 'auto' }}>Uploaded ✓</span>
                            </>
                        ) : (
                            <>
                                <span className="spinner-border spinner-border-sm text-success" role="status" style={{ width: 12, height: 12 }} />
                                <span style={{ color: '#6b7280', fontSize: 12 }}>Uploading {fileName}…</span>
                            </>
                        )}
                    </div>

                    {/* hidden input so user can pick a new file */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png"
                        onChange={handleInputChange}
                        style={{ display: 'none' }}
                    />
                </div>
            )}

            {/* ── Error message ── */}
            {error && (
                <div className="meta-uploader-error">
                    <MDBIcon fas icon="exclamation-circle" />
                    <span>{error}</span>
                </div>
            )}
        </div>
    );
};

export default MediaUploader;